// n8n triggers: the editor (name, agent, prompt template with placeholder help, sample payload with a
// live rendered preview, autonomy, output format with its JSON example, callback URL + secret), the
// secret shown ONCE after creation / rotation, the "Intégrer dans n8n" help (hook URL, HTTP Request
// node settings, curl) and the TEST dialog (POST /api/triggers/:id/test) linking to the new task.
import { h, button, field, input, textarea, switchToggle, setText, iconButton } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import {
  triggerForm, validateTriggerForm, triggerBody, triggerErrorField, createdTaskId, TRIGGER_NAME_MAX, TRIGGER_TEMPLATE_MAX,
  JSON_EXAMPLE_MAX,
} from './model.js';
import {
  previewTemplate, parseJsonObject, payloadPaths, skeletonFromTemplate, hookUrl, curlExample, n8nNodeSettings, prettyJson,
  insertAt,
} from './template.js';
import {
  agentSelect, autonomyControl, formatControl, counter, codeBlock, copyText, storageGet, storageSet, TRIGGER_SAMPLE_PREFIX,
} from './widgets.js';
import './strings.js';

const SAMPLE_KEY = TRIGGER_SAMPLE_PREFIX;
const NEW_SAMPLE_KEY = `${SAMPLE_KEY}_new`;

/** The sample payload remembered in this browser for a trigger (per-viewer convenience). */
export function loadSample(triggerId, template = '') {
  const saved = storageGet(SAMPLE_KEY + (triggerId || '_new'));
  if (saved && parseJsonObject(saved).value) return saved;
  return prettyJson(skeletonFromTemplate(template));
}

function saveSample(triggerId, text) {
  storageSet(SAMPLE_KEY + (triggerId || '_new'), text && text.length <= 20000 ? text : null);
}

/** Forget the sample payload remembered for a trigger (deleted trigger). */
export function forgetSample(triggerId) {
  if (triggerId) storageSet(SAMPLE_KEY + triggerId, null);
}

function effectiveAuto(trigger, state) {
  if (trigger?.autonomy === 'auto') return true;
  if (trigger?.autonomy !== 'inherit' && trigger?.autonomy) return false;
  const agent = (state?.office?.agents || []).find((a) => a.id === trigger?.agentId);
  return agent?.autonomy === 'auto';
}

function hookUrlOf(trigger) {
  return typeof trigger?.hookUrl === 'string' && /^https?:\/\//.test(trigger.hookUrl)
    ? trigger.hookUrl : hookUrl(trigger?.id, globalThis.location || null);
}

/**
 * The "Intégrer dans n8n" content: hook URL, HTTP Request node settings, JSON body and curl example.
 * The secret itself is never written into these snippets.
 * @param {object} ctx
 * @param {object} trigger
 * @param {{payload?: object}} [opts]
 * @returns {HTMLElement}
 */
export function integrationSection(ctx, trigger, { payload } = {}) {
  const url = hookUrlOf(trigger);
  const sample = payload ?? (parseJsonObject(loadSample(trigger?.id, trigger?.promptTemplate)).value || {});
  const wait = effectiveAuto(trigger, ctx.store.get());
  const rows = n8nNodeSettings(url, { wait });
  return h('section', { class: 'auto-int', 'aria-label': t('auto.int.title') },
    h('h3', { class: 'auto-int-title' }, t('auto.int.title')),
    codeBlock(ctx, url, { label: t('auto.int.url') }),
    h('div', { class: 'auto-codewrap' },
      h('p', { class: 'mono-label auto-codelabel' }, t('auto.int.node')),
      h('dl', { class: 'auto-kv' }, rows.map((r) => h('div', { class: 'auto-kv-row' },
        h('dt', {}, r.label), h('dd', {}, r.code ? h('code', { class: 'auto-code' }, r.value) : r.value))))),
    codeBlock(ctx, prettyJson(sample), { label: t('auto.int.body'), multiline: true }),
    codeBlock(ctx, curlExample(url, sample), { label: t('auto.int.curl'), multiline: true }),
    trigger?.inboxDir ? h('div', { class: 'auto-codewrap auto-inbox', role: 'group', 'aria-label': t('auto.int.files') },
      h('p', { class: 'mono-label auto-codelabel' }, t('auto.int.files')),
      codeBlock(ctx, String(trigger.inboxDir), { label: t('auto.int.inbox') }),
      h('p', { class: 'field-hint' }, t('auto.int.filesHint'))) : null,
    wait ? h('p', { class: 'field-hint' }, t('auto.int.waitNote')) : null,
    h('p', { class: 'field-hint' }, t('auto.int.answers')),
    h('p', { class: 'field-hint' }, t('auto.int.docker')));
}

/**
 * Shows a trigger secret ONCE (after creation or rotation): highlighted box, copy buttons, warning,
 * then the n8n integration help. Not dismissible by Esc/backdrop: the user confirms having copied it.
 * @param {object} ctx
 * @param {{trigger: object, secret: string, rotated?: boolean}} opts
 * @returns {import('../overlay.js').Layer}
 */
export function openSecretReveal(ctx, { trigger, secret, rotated = false }) {
  let value = String(secret ?? '');
  const code = h('code', { class: 'auto-secret-value', 'aria-label': t('auto.secret.aria'), tabIndex: 0 }, value);
  const box = h('div', { class: 'auto-secret', role: 'group', 'aria-label': t('auto.secret.label') },
    h('p', { class: 'auto-secret-warning', role: 'alert' }, t('auto.secret.warning')),
    h('div', { class: 'auto-secret-row' }, code),
    h('div', { class: 'row auto-secret-actions' },
      button(t('auto.copy'), { variant: 'primary', size: 'sm', icon: 'check', onClick: () => copyText(ctx, value) }),
      button(t('auto.secret.copyBearer'), { variant: 'secondary', size: 'sm', onClick: () => copyText(ctx, `Bearer ${value}`) })),
    h('p', { class: 'field-hint' }, t('auto.secret.where')));
  const layer = ctx.overlay.openModal({
    title: rotated ? t('auto.secret.titleRotated') : t('auto.secret.title'),
    eyebrow: trigger?.name || trigger?.id || '',
    size: 'md', className: 'auto-modal', dismissible: false,
    content: h('div', { class: 'stack' }, box, integrationSection(ctx, trigger)),
    footer: [button(t('auto.secret.done'), { variant: 'primary', onClick: () => layer.close('confirm') })],
  });
  // Forget the secret as soon as the dialog closes.
  layer.onClose(() => { value = ''; code.textContent = ''; });
  return layer;
}

/**
 * The integration help alone (from the list, no secret).
 * @returns {import('../overlay.js').Layer}
 */
export function openIntegration(ctx, trigger) {
  let layer = null;
  layer = ctx.overlay.openModal({
    title: t('auto.int.title'), eyebrow: trigger?.name || '', size: 'md', className: 'auto-modal',
    content: integrationSection(ctx, trigger),
    footer: [button(t('common.close'), { variant: 'primary', onClick: () => layer?.close('button') })],
  });
  return layer;
}

/**
 * The TEST dialog: sends a sample payload (POST /api/triggers/:id/test) and links to the task created.
 * @param {object} ctx
 * @param {object} trigger
 * @returns {import('../overlay.js').Layer}
 */
export function openTriggerTest(ctx, trigger) {
  let layer = null;
  const payloadInput = textarea({ value: loadSample(trigger.id, trigger.promptTemplate), rows: 8, class: 'auto-mono', spellcheck: false });
  const payloadField = field(t('auto.test.payload'), payloadInput);
  const preview = h('pre', { class: 'auto-preview' });
  const result = h('div', { class: 'auto-test-result', 'aria-live': 'polite' });
  const sendBtn = button(t('auto.test.send'), { variant: 'primary', icon: 'send', onClick: () => send() });

  function refresh() {
    const { value, error } = parseJsonObject(payloadInput.value);
    payloadField.setError(error);
    sendBtn.disabled = !!error;
    const pv = previewTemplate(trigger.promptTemplate || '', value || {});
    setText(preview, pv.text || t('auto.te.previewEmpty'));
  }
  payloadInput.addEventListener('input', refresh);

  async function send() {
    const { value, error } = parseJsonObject(payloadInput.value);
    if (error) { payloadField.setError(error); payloadInput.focus(); return; }
    saveSample(trigger.id, payloadInput.value);
    sendBtn.disabled = true;
    sendBtn.classList.add('is-busy');
    result.replaceChildren();
    try {
      const res = await ctx.api.post(`/api/triggers/${encodeURIComponent(trigger.id)}/test`, { payload: value });
      if (res?.id && res.state) ctx.store.applyTask?.(res);
      const taskId = createdTaskId(res);
      ctx.toast.success(t('auto.test.sent'));
      result.replaceChildren(h('div', { class: 'auto-test-ok' },
        h('p', {}, taskId ? t('auto.test.result', { id: taskId }) : t('auto.test.sent')),
        taskId ? button(t('auto.test.openTask'), {
          variant: 'secondary', size: 'sm', iconRight: 'arrowRight',
          onClick: () => { layer.close('select'); ctx.navigate(`#/task/${encodeURIComponent(taskId)}`); },
        }) : null));
    } catch (err) {
      result.replaceChildren(h('p', { class: 'auto-form-error', role: 'alert' }, err.message));
    } finally {
      sendBtn.disabled = false;
      sendBtn.classList.remove('is-busy');
    }
  }

  refresh();
  layer = ctx.overlay.openModal({
    title: t('auto.test.title', { name: trigger.name || trigger.id }), size: 'md', className: 'auto-modal',
    content: h('div', { class: 'stack' },
      h('p', { class: 'muted' }, t('auto.test.intro')),
      trigger.enabled === false ? h('p', { class: 'auto-note' }, t('auto.test.disabledNote')) : null,
      payloadField,
      h('div', { class: 'field' }, h('span', { class: 'field-label' }, t('auto.te.preview')), preview),
      result),
    footer: [button(t('common.close'), { variant: 'ghost', onClick: () => layer.close('button') }), sendBtn],
    initialFocus: payloadInput,
  });
  return layer;
}

/**
 * Opens the trigger editor.
 * @param {object} ctx
 * @param {{trigger?: object|null, onSaved?: (trigger: object, secret?: string) => void}} [opts]
 * @returns {import('../overlay.js').Layer}
 */
export function openTriggerEditor(ctx, { trigger = null, onSaved, inboxDir = '' } = {}) {
  const editing = !!trigger?.id;
  const state = ctx.store.get();
  const form = triggerForm(trigger);
  // Where n8n drops the input files a call names in "files" (GET /api/triggers gives it per trigger;
  // a new trigger borrows the one of the list).
  const inbox = String(trigger?.inboxDir || inboxDir || '');
  const agentOf = (id) => ctx.store.get().office.agents.find((a) => a.id === id) || null;
  let layer = null;
  let busy = false;

  const nameInput = input({ value: form.name, maxLength: TRIGGER_NAME_MAX + 20, placeholder: t('auto.te.namePlaceholder') });
  const nameField = field(t('auto.te.name'), nameInput);
  const agentEl = agentSelect(state, { need: 'manage', value: form.agentId, extra: editing ? agentOf(form.agentId) : null });
  const agentField = field(t('auto.agent'), agentEl);
  const enabledSwitch = switchToggle(t('auto.te.enabled'), { checked: form.enabled, hint: t('auto.te.enabledHint') });

  const templateInput = textarea({ value: form.promptTemplate, rows: 6, maxLength: TRIGGER_TEMPLATE_MAX + 200, placeholder: t('auto.te.templatePlaceholder'), spellcheck: true });
  const templateField = field(t('auto.te.template'), templateInput, { hint: t('auto.te.templateHint') });
  templateField.querySelector('.field-label')?.appendChild(counter(templateInput, TRIGGER_TEMPLATE_MAX));
  const chips = h('div', { class: 'auto-placeholders', role: 'group', 'aria-label': t('auto.te.placeholders') });
  const chipsBox = h('div', { class: 'auto-placeholder-box' }, h('p', { class: 'mono-label auto-codelabel' }, t('auto.te.placeholders')), chips);

  const sampleInput = textarea({ value: loadSample(trigger?.id, form.promptTemplate), rows: 7, class: 'auto-mono', spellcheck: false });
  const sampleField = field(t('auto.te.sample'), sampleInput, { hint: t('auto.te.sampleHint') });
  const sampleBtn = button(t('auto.te.sampleFromTemplate'), {
    variant: 'ghost', size: 'sm', icon: 'refresh',
    onClick: () => { sampleInput.value = prettyJson(skeletonFromTemplate(templateInput.value)); refreshPreview(); },
  });
  const preview = h('pre', { class: 'auto-preview', 'aria-live': 'polite' });
  const missingLine = h('p', { class: 'field-hint auto-missing', hidden: true });

  const autonomy = autonomyControl({ value: form.autonomy, agent: () => agentOf(agentEl.value) });
  const jsonExampleInput = textarea({ value: form.jsonExample, rows: 4, class: 'auto-mono', maxLength: JSON_EXAMPLE_MAX + 200, placeholder: t('auto.te.jsonExamplePlaceholder'), spellcheck: false });
  const jsonExampleField = field(t('auto.te.jsonExample'), jsonExampleInput, { hint: t('auto.te.jsonExampleHint'), optional: true });
  const format = formatControl({ value: form.outputFormat, onChange: (v) => { jsonExampleField.hidden = v !== 'json'; } });
  jsonExampleField.hidden = form.outputFormat !== 'json';
  const inboxBox = inbox ? h('div', { class: 'auto-inbox', role: 'group', 'aria-label': t('auto.int.files') },
    codeBlock(ctx, inbox, { label: t('auto.int.inbox') }),
    h('p', { class: 'field-hint' }, t('auto.int.inboxHint'))) : null;

  const cbUrlInput = input({ type: 'url', value: form.callbackUrl, placeholder: t('auto.te.callbackUrlPlaceholder'), inputmode: 'url', autocomplete: 'off', spellcheck: false });
  const cbUrlField = field(t('auto.te.callbackUrl'), cbUrlInput, { hint: t('auto.te.callbackUrlHint'), optional: true });
  const cbSecretInput = input({ type: 'password', autocomplete: 'new-password', spellcheck: false, maxLength: 256,
    placeholder: editing && form.hasCallbackSecret ? '••••••••' : '' });
  const toggleSecretBtn = iconButton('eye', t('auto.te.show'), {
    variant: 'ghost',
    onClick: () => {
      const show = cbSecretInput.type === 'password';
      cbSecretInput.type = show ? 'text' : 'password';
      toggleSecretBtn.setAttribute('aria-label', show ? t('auto.te.hide') : t('auto.te.show'));
      toggleSecretBtn.setAttribute('title', show ? t('auto.te.hide') : t('auto.te.show'));
    },
  });
  const secretWrap = h('div', { class: 'auto-secret-input' }, cbSecretInput, toggleSecretBtn);
  secretWrap.input = cbSecretInput;
  const cbSecretHint = editing && form.hasCallbackSecret
    ? `${t('auto.te.callbackSecretHint')} ${t('auto.te.callbackSecretSet')} ${t('auto.te.callbackSecretKeep')}`
    : t('auto.te.callbackSecretHint');
  const cbSecretField = field(t('auto.te.callbackSecret'), secretWrap, { hint: cbSecretHint, optional: true });
  const clearSecretSwitch = editing && form.hasCallbackSecret ? switchToggle(t('auto.te.callbackSecretClear')) : null;
  const callbackBox = h('fieldset', { class: 'auto-sched auto-callback' },
    h('legend', { class: 'mono-label auto-legend' }, t('auto.te.callback')),
    h('p', { class: 'field-hint' }, t('auto.te.callbackIntro')),
    cbUrlField, cbSecretField, clearSecretSwitch);

  const formError = h('p', { class: 'auto-form-error', role: 'alert', hidden: true });
  const fields = { name: nameField, agentId: agentField, promptTemplate: templateField, jsonExample: jsonExampleField, callbackUrl: cbUrlField, callbackSecret: cbSecretField };

  function setFormError(msg) {
    setText(formError, msg || '');
    formError.hidden = !msg;
  }

  // --- placeholder help + live preview -------------------------------------------------------------
  function insertPlaceholder(path) {
    const snippet = `{{${path}}}`;
    const start = Number.isInteger(templateInput.selectionStart) ? templateInput.selectionStart : templateInput.value.length;
    const end = Number.isInteger(templateInput.selectionEnd) ? templateInput.selectionEnd : start;
    const next = insertAt(templateInput.value, snippet, start, end);
    templateInput.value = next.value;
    templateInput.focus();
    try { templateInput.setSelectionRange?.(next.caret, next.caret); } catch { /* not a text control */ }
    templateInput.dispatchEvent?.(new Event('input'));
    refreshPreview();
  }

  function refreshPreview() {
    const { value, error } = parseJsonObject(sampleInput.value);
    sampleField.setError(error);
    const payload = value || {};
    const paths = [...payloadPaths(payload), 'now'];
    chips.replaceChildren(...paths.map((p) => h('button', {
      type: 'button', class: 'auto-ph', title: p === 'now' ? t('auto.te.now') : `{{${p}}}`,
      onClick: () => insertPlaceholder(p),
    }, `{{${p}}}`)));
    if (paths.length === 1) chips.appendChild(h('span', { class: 'field-hint' }, t('auto.te.placeholdersEmpty')));
    const pv = previewTemplate(templateInput.value, payload);
    setText(preview, pv.text.trim() ? pv.text : t('auto.te.previewEmpty'));
    preview.classList.toggle('is-empty', !pv.text.trim());
    const missing = pv.missing.filter((p) => p !== 'now');
    missingLine.hidden = !missing.length || !!error;
    setText(missingLine, missing.length ? t('auto.te.missing', { n: missing.length, list: missing.map((p) => `{{${p}}}`).join(', ') }) : '');
  }

  templateInput.addEventListener('input', () => { fields.promptTemplate.setError(null); refreshPreview(); });
  sampleInput.addEventListener('input', refreshPreview);
  agentEl.addEventListener('change', () => { agentField.setError(null); autonomy.refresh(); });
  nameInput.addEventListener('input', () => nameField.setError(null));
  cbUrlInput.addEventListener('input', () => cbUrlField.setError(null));
  cbSecretInput.addEventListener('input', () => cbSecretField.setError(null));

  // --- save ------------------------------------------------------------------------------------------
  const saveBtn = button(editing ? t('auto.te.save') : t('auto.te.create'), { variant: 'primary', onClick: () => save() });
  const cancelBtn = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });

  function readForm() {
    form.name = nameInput.value;
    form.agentId = agentEl.value;
    form.enabled = enabledSwitch.input.checked;
    form.promptTemplate = templateInput.value;
    form.autonomy = autonomy.value;
    form.outputFormat = format.value;
    form.jsonExample = jsonExampleInput.value;
    form.callbackUrl = cbUrlInput.value;
    form.callbackSecret = cbSecretInput.value;
    form.clearCallbackSecret = !!clearSecretSwitch?.input.checked;
  }

  const ORDER = ['name', 'agentId', 'promptTemplate', 'jsonExample', 'callbackUrl', 'callbackSecret'];
  async function save() {
    if (busy) return;
    readForm();
    setFormError('');
    const errors = validateTriggerForm(form, ctx.store.get());
    for (const name of ORDER) fields[name]?.setError(errors[name] || null);
    const firstBad = ORDER.find((n) => errors[n]);
    if (firstBad) {
      setFormError(t('auto.fixErrors'));
      const ctl = { name: nameInput, agentId: agentEl, promptTemplate: templateInput, jsonExample: jsonExampleInput, callbackUrl: cbUrlInput, callbackSecret: cbSecretInput }[firstBad];
      ctl?.focus();
      return;
    }
    busy = true;
    saveBtn.disabled = true;
    saveBtn.classList.add('is-busy');
    layer.setBusy?.(true);
    try {
      const body = triggerBody(form, ctx.store.get(), { editing });
      let saved;
      let secret = '';
      if (editing) {
        const res = await ctx.api.patch(`/api/triggers/${encodeURIComponent(trigger.id)}`, body);
        saved = res?.trigger && typeof res.trigger === 'object' ? res.trigger : (res && typeof res === 'object' ? res : { ...trigger, ...body });
        ctx.toast.success(t('auto.te.saved'));
      } else {
        const res = await ctx.api.post('/api/triggers', body);
        saved = res?.trigger && typeof res.trigger === 'object' ? res.trigger : res;
        secret = typeof res?.secret === 'string' ? res.secret : '';
        ctx.toast.success(t('auto.te.created'));
      }
      if (saved?.id) {
        saveSample(saved.id, sampleInput.value);
        if (!editing) storageSet(NEW_SAMPLE_KEY, null);
      }
      layer.close('confirm');
      try { onSaved?.(saved, secret); } catch (err) { console.error(err); }
    } catch (err) {
      const where = triggerErrorField(err);
      if (where !== 'form' && fields[where]) {
        fields[where].setError(err.message);
        setFormError(t('auto.fixErrors'));
      } else setFormError(err.message || t('auto.saveError'));
    } finally {
      busy = false;
      saveBtn.disabled = false;
      saveBtn.classList.remove('is-busy');
      layer?.setBusy?.(false);
    }
  }

  const content = h('form', { class: 'auto-form stack', novalidate: true, onSubmit: (e) => { e.preventDefault(); save(); } },
    h('div', { class: 'auto-grid2' }, nameField, agentField),
    enabledSwitch,
    templateField,
    chipsBox,
    h('div', { class: 'auto-grid2 auto-sample' },
      h('div', { class: 'stack auto-tight' }, sampleField, sampleBtn),
      h('div', { class: 'field' }, h('span', { class: 'field-label' }, t('auto.te.preview')), preview, missingLine)),
    h('div', { class: 'auto-grid2' }, autonomy.el, format.el),
    jsonExampleField,
    inboxBox,
    callbackBox,
    formError);

  refreshPreview();
  layer = ctx.overlay.openModal({
    title: editing ? t('auto.te.titleEdit') : t('auto.te.titleNew'),
    eyebrow: editing ? (trigger.name || trigger.id) : null,
    size: 'lg', className: 'auto-modal', content,
    footer: [cancelBtn, saveBtn],
    initialFocus: nameInput,
  });
  // Unsaved sample of a new trigger survives a cancel (per browser).
  if (!editing) layer.onClose((reason) => { if (reason !== 'confirm' && sampleInput.value.trim()) saveSample(null, sampleInput.value); });
  return layer;
}
